import * as React from 'react';
import { FC, useCallback, useEffect, useState } from 'react';

import { Box, Header, Pressable, Text } from '@rbilabs/universal-components';
import queryString from 'query-string';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

import { LayoutContainer } from 'components/layout/styled';
import LoadingAnimation from 'components/loading-animation';
import Logo from 'components/logo';
import ConfettiFriesAnimation, {
  useConfettiFriesAnimation,
} from 'components/lottie-animations/confetti-fries';
import {
  useLoyaltyLinkWalmartAccountMutation,
  useLoyaltyWalmartEligibilityLazyQuery,
} from 'generated/graphql-gateway';
import useAuth from 'hooks/auth/use-auth';
import { useNavigation } from 'hooks/navigation/use-navigation';
import { ModalAuthScreen } from 'state/auth/types';
import { useLoyaltyUserState } from 'state/loyalty/hooks/use-loyalty-user-state';
import { isActiveWalmartUser } from 'state/loyalty/hooks/utils/walmart';
import { portalToFooter } from 'state/mobile-footer';
import { portalToReplaceHeader } from 'state/mobile-header-nav';
import { useUIContext } from 'state/ui';
import { routes } from 'utils/routing';

import { WalmartWelcomeBack } from '../walmart-welcome-back';

import { EligibilityErrorModal, WalmartEligibilityErrors } from './eligibility-error-modal';
import { StyledActionButton, StyledBox, StyledLogo } from './styled';

export const WalmartLandingPage: FC<React.PropsWithChildren<{}>> = () => {
  const { formatMessage } = useIntl();
  const location = useLocation();
  const { navigate, setParams } = useNavigation();
  const { triggerAnimation } = useConfettiFriesAnimation();
  const { loyaltyUser, loading: loyaltyUserLoading } = useLoyaltyUserState();
  const { setUserEmail, userEmail } = useUIContext();
  const {
    isAuthenticated,
    setModalAuthState,
    closeModalAuth,
    signOut,
    user,
    loading: authUserLoading,
  } = useAuth();

  const [openWelcomeBackDrawer, setOpenWelcomeBackDrawer] = useState(false);
  const [shouldLink, setShouldLink] = useState(false);

  const [
    getEligibility,
    {
      loading: eligibilityLoading,
      error: eligibilityError,
      data: eligibilityData,
      called: eligibilityCalled,
    },
  ] = useLoyaltyWalmartEligibilityLazyQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'standby',
  });
  const [
    linkWalmartAccount,
    { loading: linkLoading, error: linkError, data: linkData, called: linkCalled },
  ] = useLoyaltyLinkWalmartAccountMutation();
  const [eligibilityErrorCode, setEligibilityErrorCode] = useState<WalmartEligibilityErrors>();

  const queryParams = queryString.parse(location.search);
  const authzCode = queryParams.authz || queryParams.code || queryParams.AuthZCode;

  const isAlreadyLinked = isActiveWalmartUser(loyaltyUser);
  const eligibility = eligibilityData?.loyaltyGetWalmartEligibility;

  const isLoading = authUserLoading || loyaltyUserLoading || eligibilityLoading || linkLoading;

  const handleRedirectToLandingPage = () => {
    setEligibilityErrorCode(undefined);
    navigate(routes.walmart);
  };

  const checkEligibility = useCallback(async () => {
    try {
      if (!authzCode || typeof authzCode !== 'string') {
        return setEligibilityErrorCode(WalmartEligibilityErrors.DEFAULT);
      }
      if (
        !isAlreadyLinked &&
        !eligibilityCalled &&
        !eligibilityLoading &&
        !eligibilityData &&
        !eligibilityError
      ) {
        const { data, error } = await getEligibility({
          variables: {
            input: {
              authz: authzCode,
              loyaltyId: user?.loyaltyId || undefined,
            },
          },
        });
        if (error) {
          setEligibilityErrorCode(WalmartEligibilityErrors.DEFAULT);
          return;
        }
        const eligibility = data?.loyaltyGetWalmartEligibility;
        if (eligibility) {
          const { eligible, notEligibleReason, email } = eligibility;
          if (!eligible) {
            return setEligibilityErrorCode(
              (notEligibleReason as WalmartEligibilityErrors) || WalmartEligibilityErrors.DEFAULT
            );
          }
          if (!isAuthenticated) {
            setUserEmail(email);
          }
        }
      }
    } catch (err) {
      return setEligibilityErrorCode(WalmartEligibilityErrors.DEFAULT);
    }
  }, [
    authzCode,
    isAlreadyLinked,
    eligibilityCalled,
    eligibilityLoading,
    eligibilityData,
    eligibilityError,
    getEligibility,
    user?.loyaltyId,
    isAuthenticated,
    setUserEmail,
  ]);

  const switchAccount = useCallback(async () => {
    setOpenWelcomeBackDrawer(false);
    if (isAuthenticated) {
      await signOut();
    }
    setUserEmail(eligibility?.email || '');
    setModalAuthState({
      screen: ModalAuthScreen.SIGN_IN,
      onSignInSuccess: async () => {
        closeModalAuth();
        setShouldLink(true);
      },
    });
  }, [
    closeModalAuth,
    eligibility?.email,
    isAuthenticated,
    setModalAuthState,
    setUserEmail,
    signOut,
  ]);

  const linkAccount = useCallback(async () => {
    if (!eligibility) {
      return setEligibilityErrorCode(WalmartEligibilityErrors.DEFAULT);
    }

    if (eligibility.isExistingUser && !isAuthenticated) {
      return switchAccount();
    }

    setOpenWelcomeBackDrawer(false);

    try {
      const { data, errors } = await linkWalmartAccount({
        variables: {
          input: {
            accessToken: eligibility.accessToken,
            refreshToken: eligibility.refreshToken,
            loyaltyId: user?.loyaltyId || '',
          },
        },
      });

      if (errors) {
        setEligibilityErrorCode(WalmartEligibilityErrors.DEFAULT);
      } else if (data) {
        const { success, failureReason } = data.loyaltyLinkWalmartAccount;
        if (success) {
          triggerAnimation();
        } else {
          setEligibilityErrorCode(
            (failureReason as WalmartEligibilityErrors) || WalmartEligibilityErrors.DEFAULT
          );
        }
      }
    } catch {
      setEligibilityErrorCode(WalmartEligibilityErrors.DEFAULT);
    }
  }, [
    eligibility,
    isAuthenticated,
    linkWalmartAccount,
    switchAccount,
    triggerAnimation,
    user?.loyaltyId,
  ]);

  // Check eligibility once user data stops loading
  useEffect(() => {
    if (
      authzCode &&
      !loyaltyUserLoading &&
      !authUserLoading &&
      !eligibilityCalled &&
      !isAlreadyLinked
    ) {
      checkEligibility();
    }
  }, [
    loyaltyUserLoading,
    authUserLoading,
    checkEligibility,
    eligibilityCalled,
    isAlreadyLinked,
    authzCode,
  ]);

  // Perform link after user switches accounts
  useEffect(() => {
    if (shouldLink && user && !authUserLoading && !linkCalled) {
      linkAccount();
    }
  }, [authUserLoading, shouldLink, linkAccount, linkCalled, user, loyaltyUser]);

  // Close auth modal when leaving page
  useEffect(() => {
    return () => {
      closeModalAuth();
    };
  }, [closeModalAuth]);

  const HiddenAppHeader = portalToReplaceHeader(() => null);
  const HiddenAppFooter = portalToFooter(() => {
    if (isAlreadyLinked || linkData?.loyaltyLinkWalmartAccount.success) {
      return (
        <Box padding="$4">
          <StyledActionButton
            onPress={() => {
              setParams({ success: 'true' });
              navigate(`${routes.loyaltyOfferList}?success=true`);
            }}
          >
            {formatMessage({ id: 'viewOffers' })}
          </StyledActionButton>
        </Box>
      );
    }

    if (!authzCode) {
      return (
        <Box padding="$4">
          <StyledActionButton
            onPress={() => {
              // TODO: We are waiting on what URL to use to direct user to Walmart
              alert('TODO: this should link to Walmart');
            }}
          >
            {formatMessage({ id: 'walmartLinkAccounts' })}
          </StyledActionButton>
        </Box>
      );
    }

    return eligibility?.eligible && !linkCalled ? (
      <Box padding="$4">
        <StyledActionButton
          onPress={() => {
            if (!isAuthenticated && !eligibility.isExistingUser) {
              return setModalAuthState({
                screen: ModalAuthScreen.SIGN_IN,
                onSignInSuccess: () => {
                  closeModalAuth();
                  setShouldLink(true);
                },
              });
            }
            return setOpenWelcomeBackDrawer(true);
          }}
        >
          {!isAuthenticated && !eligibility.isExistingUser
            ? formatMessage({ id: 'walmartSignUpSignIn' })
            : formatMessage({ id: 'walmartLinkAccounts' })}
        </StyledActionButton>
      </Box>
    ) : null;
  });

  return (
    <LayoutContainer isFullContainer={true}>
      <ConfettiFriesAnimation />
      <StyledBox>
        <Pressable onPress={() => navigate(routes.base)}>
          <StyledLogo>
            <Logo padding="0" />
          </StyledLogo>
        </Pressable>
        <Header paddingTop="$4">Walmart+</Header>
        {isAlreadyLinked ? (
          <Text>Already linked</Text>
        ) : (
          <>
            <Text>AuthZ: {authzCode || 'Not found'}</Text>
            <Text>Logged in with: {user?.details.email || 'Unauthenticated'} </Text>
            <Text>Walmart email: {eligibility?.email} </Text>
          </>
        )}
        {eligibilityLoading && <Text>Loading...</Text>}
        {eligibility && !eligibility.eligible && (
          <Text>Not eligible: {eligibility.notEligibleReason}</Text>
        )}
        {!eligibility && eligibilityError && (
          <Text>Eligibility error: {eligibilityError.message}</Text>
        )}
        {linkLoading && <Text>Linking account...</Text>}
        {linkError && (
          <>
            <Text>Link Error</Text>
            <pre>{JSON.stringify(linkError, null, 2)}</pre>
          </>
        )}
        {linkData && linkData.loyaltyLinkWalmartAccount.success && <Text>Link success</Text>}
        {isLoading && <LoadingAnimation />}
      </StyledBox>

      <HiddenAppHeader />
      <HiddenAppFooter />
      <WalmartWelcomeBack
        isOpen={openWelcomeBackDrawer}
        handleOnClose={() => setOpenWelcomeBackDrawer(false)}
        accountEmail={user?.details?.email || userEmail || undefined}
        handleLinkAccount={() => linkAccount()}
        handleSwitchAccount={() => switchAccount()}
      />
      <EligibilityErrorModal
        errorCode={eligibilityErrorCode}
        onTryAgain={handleRedirectToLandingPage}
        onDismiss={() => setEligibilityErrorCode(undefined)}
        user={user}
      />
    </LayoutContainer>
  );
};
